/**
 * Korting op een kostenregel in de wizards — afstandskosten, adoptiekosten, retourkosten.
 *
 * <p><b>Per regel en niet per stap.</b> Zo draagt de korting vanzelf het btw-tarief van de regel waar
 * hij op zit, en kun je op het ene product wel korting geven en op het andere niet (Maarten,
 * 2026-10-24). Eén bedrag onderaan zou over meerdere tarieven verdeeld moeten worden, en dat is
 * rekenwerk dat niemand kan controleren.
 *
 * <p><b>Nooit meer dan de regel zelf.</b> Een regel die onder nul duikt is geen korting maar een
 * terugbetaling, en die hoort via crediteren te lopen. Te veel korting wordt daarom geweigerd en niet
 * stil afgekapt: klemmen zou een typefout van € 1.000 veranderen in "gratis" zonder dat iemand het ziet.
 */

/** Een kostenregel zoals de wizards hem bijhouden; alleen wat deze regels nodig hebben. */
export interface KostenRegel {
  amount: string;
  korting?: string;
}

/** Twee decimalen, één keer afgerond — anders stapelen centfouten op in het totaal. */
export function afgerondeEuro(bedrag: number): number {
  return Math.round(bedrag * 100) / 100;
}

/** "12,50" en "12.50" zijn allebei twaalf vijftig; leeg of onzin is geen bedrag. */
function alsBedrag(waarde: string | undefined): number {
  const schoon = (waarde ?? "").trim().replace(",", ".");
  if (!schoon) return 0;
  const n = Number(schoon);
  return Number.isFinite(n) && n > 0 ? afgerondeEuro(n) : 0;
}

/** De korting van één regel zoals hij meetelt: nooit meer dan de regel zelf. */
export function kortingVanRegel(regel: KostenRegel): number {
  return Math.min(alsBedrag(regel.korting), Math.max(0, alsBedrag(regel.amount)));
}

/** Of er op deze regel meer korting staat dan er te verrekenen valt. */
export function kortingRegelTeHoog(regel: KostenRegel): boolean {
  return alsBedrag(regel.korting) - Math.max(0, alsBedrag(regel.amount)) > 0.005;
}

/** Alle korting van een stap bij elkaar — voor het totaal en de samenvatting. */
export function kortingTotaal(regels: KostenRegel[]): number {
  return afgerondeEuro(regels.reduce((som, r) => som + kortingVanRegel(r), 0));
}

/** Wat deze regel na korting kost. */
export function regelNaKorting(regel: KostenRegel): number {
  return afgerondeEuro(Math.max(0, alsBedrag(regel.amount)) - kortingVanRegel(regel));
}
